/**
 * FREngine — ядро ретуши лица: снятие блика (де-блеск) на физической модели.
 *
 * ФИЗИКА, А НЕ ПОДАВЛЕНИЕ ЯРКОСТИ
 * ───────────────────────────────
 * Снятое изображение раскладывается по дихроматической модели:
 *
 *        I = d·γ + S·λ
 *
 * где γ — хроматичность КОЖИ в этой точке, λ — хроматичность СВЕТА, d —
 * яркость кожи (затенение), S — величина блика. Убрать блеск = найти S и
 * вычесть S·λ, оставив d·γ. Это принципиально не то же самое, что «притушить
 * светлые пиксели»: последнее уносит вместе с бликом и яркость самой кожи,
 * отчего лоб становится серым пятном.
 *
 * ЧТО ИЗМЕРЕНО (tests/scratch/deshine-*-scratch.mjs, корпус реальных лиц +
 * дорожка с известным ответом)
 * ───────────────────────────────────────────────────────────────────────────
 * · Решатель при точных γ и λ возвращает исходный цвет с ошибкой ~1e-14 —
 *   модель верна, и ошибки итога это ошибки ОЦЕНОК, а не формулы.
 * · Разделимость ‖γ−λ‖ — физический предел задачи. Под белым светом она 0.158,
 *   под лампой накаливания 0.034: тот же 8-битный шум раздувается в 4.6 раза,
 *   и ошибка цвета растёт с 0.005 до 0.045. Поэтому величина блика оценивается
 *   на СГЛАЖЕННОМ поле (блик плавный, шум — нет), а сама разделимость
 *   выносится наружу: если она мала, никакой алгоритм здесь не разделит, и об
 *   этом надо сказать, а не выдать серое пятно.
 * · Оценщик наклона (t_k = I_k/γ_k линеен по ρ_k = λ_k/γ_k, наклон = S)
 *   инвариантен к ЗАТЕНЕНИЮ: тень меняет свободный член, а не наклон. Поэтому
 *   тёмная и светлая части лица обрабатываются одинаково честно.
 * · Обрезанный канал НЕ участвует в подгонке. Двух живых каналов хватает,
 *   чтобы найти S, — поэтому частично выбитый блик восстанавливается ПО ЦВЕТУ,
 *   а не замазывается. Там, где выбиты все три, возвращать нечего, и это
 *   помечается честно (`blownFrac`).
 * · Маска кожи обязана идти от ГЕОМЕТРИИ лица. Хроматическая маска «красноватый
 *   яркий пиксель = кожа» на реальном снимке захватила фон, стену и джинсы, и
 *   алгоритм выел пятна по всему кадру. Это не настройка порога — это неверный
 *   источник границы.
 *
 * Интерфейс — window.FREngine:
 *   analyze(planes, faces, opts) → model
 *   deshine(planes, model, {amount}) → planes   (не трогает вход)
 *   toPlanes(imageData) / toImageData(planes, out)
 */
(function (root) {
    'use strict';

    var EPS = 1e-6;

    // ── sRGB ↔ линейный ─────────────────────────────────────────────────────
    var S2L = new Float32Array(256);
    for (var i = 0; i < 256; i++) {
        var c = i / 255;
        S2L[i] = c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    }
    function lin2srgbExact(v) {
        v = v < 0 ? 0 : (v > 1 ? 1 : v);
        return v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
    }
    /* Обратное преобразование — по таблице, а не по Math.pow.
     *
     * Причина замеренная: у видео кадр 608×912 стоил 590 мс, и заметная доля
     * уходила именно сюда — на кадр приходится 554 тысячи пикселей × 3 канала,
     * то есть 1.7 миллиона вызовов pow только на сборку результата. Таблица на
     * 4096 значений даёт ошибку меньше половины младшего разряда 8-битного
     * выхода, то есть на глаз и на байт результат тот же.
     */
    var L2S = new Uint8Array(4097);
    for (var li = 0; li <= 4096; li++) L2S[li] = Math.round(lin2srgbExact(li / 4096) * 255);
    function lin2srgb(v) {
        return lin2srgbExact(v);   // для внешних вызывающих — точная версия
    }
    function lin2byte(v) {
        if (!(v > 0)) return 0;
        if (v >= 1) return 255;
        return L2S[(v * 4096) | 0];
    }

    /** ImageData → линейные плоскости + карта обрезанных каналов. */
    function toPlanes(imageData) {
        var w = imageData.width, h = imageData.height, d = imageData.data, n = w * h;
        var R = new Float32Array(n), G = new Float32Array(n), B = new Float32Array(n);
        var clip = new Uint8Array(n);
        for (var i = 0, p = 0; i < n; i++, p += 4) {
            var r = d[p], g = d[p + 1], b = d[p + 2];
            R[i] = S2L[r]; G[i] = S2L[g]; B[i] = S2L[b];
            clip[i] = (r >= 250 ? 1 : 0) | (g >= 250 ? 2 : 0) | (b >= 250 ? 4 : 0);
        }
        return { w: w, h: h, R: R, G: G, B: B, clip: clip };
    }

    /** Плоскости → ImageData (можно передать существующий, чтобы не аллоцировать). */
    function toImageData(pl, out) {
        var w = pl.w, h = pl.h, n = w * h;
        var id = out || new ImageData(w, h);
        var d = id.data;
        var R = pl.R, G = pl.G, B = pl.B;
        for (var i = 0, p = 0; i < n; i++, p += 4) {
            d[p] = lin2byte(R[i]);
            d[p + 1] = lin2byte(G[i]);
            d[p + 2] = lin2byte(B[i]);
            d[p + 3] = 255;
        }
        return id;
    }

    // ── Растеризация многоугольника (чётно-нечётное правило) ────────────────
    function fillPoly(mask, w, h, pts, value, scaleAbout) {
        if (!pts || pts.length < 3) return;
        var P = pts;
        if (scaleAbout) {
            // Расширение контура от его центра — глаза и губы защищаем С ЗАПАСОМ:
            // ресницы, блик на губе и край зубов лежат ВНЕ голого контура.
            var cx = 0, cy = 0;
            for (var q = 0; q < pts.length; q++) { cx += pts[q][0]; cy += pts[q][1]; }
            cx /= pts.length; cy /= pts.length;
            P = pts.map(function (p) {
                return [cx + (p[0] - cx) * scaleAbout, cy + (p[1] - cy) * scaleAbout];
            });
        }
        var minY = 1e9, maxY = -1e9;
        for (var i = 0; i < P.length; i++) {
            var y = P[i][1] * h;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
        }
        var y0 = Math.max(0, Math.floor(minY)), y1 = Math.min(h - 1, Math.ceil(maxY));
        var xs = [];
        for (var yy = y0; yy <= y1; yy++) {
            xs.length = 0;
            var cy2 = yy + 0.5;
            for (var k = 0, m = P.length; k < m; k++) {
                var a = P[k], b = P[(k + 1) % m];
                var ay = a[1] * h, by = b[1] * h;
                if ((ay <= cy2 && by > cy2) || (by <= cy2 && ay > cy2)) {
                    var t = (cy2 - ay) / (by - ay);
                    xs.push((a[0] + t * (b[0] - a[0])) * w);
                }
            }
            if (!xs.length) continue;
            xs.sort(function (p, q) { return p - q; });
            for (var s = 0; s + 1 < xs.length; s += 2) {
                var x0 = Math.max(0, Math.ceil(xs[s] - 0.5)), x1 = Math.min(w - 1, Math.floor(xs[s + 1] - 0.5));
                var off = yy * w;
                for (var x = x0; x <= x1; x++) mask[off + x] = value;
            }
        }
    }

    // ── Разделимое коробчатое размытие ×3 ≈ гаусс ───────────────────────────
    /* ── Скретч-буферы коробчатого фильтра ────────────────────────────────
       boxBlur вызывается ~33 раза на КАЖДЫЙ кадр видео (diffuseFloor делает
       4 ступени × 4 массива и сам зовётся дважды), и каждый вызов заводил свой
       временный Float32Array на весь кадр. Буферы переиспользуются: фильтр не
       рекурсивен, параллельных вызовов нет. */
    var _bbTmp = null, _bbAcc = null;

    function boxBlur(src, w, h, r, dst) {
        r = Math.max(1, Math.round(r));
        var n = src.length;
        var cur = dst && dst.length === n ? dst : new Float32Array(n);
        if (!_bbTmp || _bbTmp.length < n) _bbTmp = new Float32Array(n);
        // ⚠️ Float64Array, а НЕ Float32Array. Бегущая сумма в старом коде была
        // обычным числом JS, то есть float64, и округлялась только при записи
        // результата. Аккумулятор во float32 округляет КАЖДЫЙ шаг: расхождение
        // выходит 8.7e-6 — мелочь на глаз, но это уже другой алгоритм, и на
        // длинном окне float32 ещё и накапливает дрейф.
        if (!_bbAcc || _bbAcc.length < w) _bbAcc = new Float64Array(w);
        var tmp = _bbTmp, acc = _bbAcc;
        // Первый горизонтальный проход читает ВХОД напрямую: прежняя копия
        // src → cur перед ним ничего не меняла в числах (все вызывающие
        // передают Float32Array, копия точная), а стоила проход по памяти.
        boxH(src, tmp, w, h, r); boxV(tmp, cur, w, h, r, acc);
        boxH(cur, tmp, w, h, r); boxV(tmp, cur, w, h, r, acc);
        boxH(cur, tmp, w, h, r); boxV(tmp, cur, w, h, r, acc);
        return cur;
    }
    /**
     * Размытие поля, которое почти всюду ноль (маска зубов, белка, зона под
     * глазами): тот же boxBlur, но только в прямоугольнике вокруг ненулевых
     * значений с запасом 3r+1. Бегущая сумма по нулям не меняется, поэтому
     * внутри прямоугольника результат побитово тот же, а снаружи — ровно ноль
     * там, где полный проход оставлял остаток сокращения порядка 1e-17. Маска
     * зубов занимает ~1 % области лица, а размывалась вся область.
     */
    function boxBlurSparse(src, w, h, r) {
        r = Math.max(1, Math.round(r));
        var x0 = w, y0 = h, x1 = -1, y1 = -1;
        for (var y = 0; y < h; y++) {
            var o = y * w;
            for (var x = 0; x < w; x++) {
                if (src[o + x] === 0) continue;
                if (x < x0) x0 = x;
                if (x > x1) x1 = x;
                if (y < y0) y0 = y;
                y1 = y;
            }
        }
        var n = w * h;
        if (x1 < 0) return new Float32Array(n);
        var m = 3 * r + 1;
        x0 = x0 - m > 0 ? x0 - m : 0; y0 = y0 - m > 0 ? y0 - m : 0;
        x1 = x1 + m < w - 1 ? x1 + m : w - 1; y1 = y1 + m < h - 1 ? y1 + m : h - 1;
        var sw = x1 - x0 + 1, sh = y1 - y0 + 1;
        if (sw * sh * 2 > n) return boxBlur(src, w, h, r);
        var sub = new Float32Array(sw * sh);
        for (var yy = 0; yy < sh; yy++) sub.set(src.subarray((y0 + yy) * w + x0, (y0 + yy) * w + x0 + sw), yy * sw);
        var b = boxBlur(sub, sw, sh, r), out = new Float32Array(n);
        for (var yb = 0; yb < sh; yb++) out.set(b.subarray(yb * sw, yb * sw + sw), (y0 + yb) * w + x0);
        return out;
    }

    /**
     * Горизонтальный проход. Середина строки — без зажимов: там окно целиком
     * внутри строки, и две проверки на КАЖДЫЙ пиксель ничего не решали. Края
     * идут прежней формулой. Слагаемые и их порядок те же, что у прежней
     * версии, — результат бит-в-бит тот же.
     */
    function boxH(s, d, w, h, r) {
        var inv = 1 / (2 * r + 1), w1 = w - 1;
        var midEnd = w - r - 2;          // последний x, у которого x+r+1 ≤ w−1
        var y = 0;
        // Середину строки ведём ЧЕТЫРЬМЯ строками сразу. Бегущая сумма — цепочка
        // зависимостей (каждый шаг ждёт предыдущий), и одна строка упирается в
        // задержку сложения, а не в память; четыре независимые цепочки процессор
        // выполняет внахлёст. Каждая строка считает то же и в том же порядке.
        if (midEnd >= r) {
            for (; y + 3 < h; y += 4) {
                var o0 = y * w, o1 = o0 + w, o2 = o1 + w, o3 = o2 + w;
                var s0 = rowHead(s, d, o0, w, r, inv), s1 = rowHead(s, d, o1, w, r, inv);
                var s2 = rowHead(s, d, o2, w, r, inv), s3 = rowHead(s, d, o3, w, r, inv);
                for (var x4 = r; x4 <= midEnd; x4++) {
                    var ad = x4 + r + 1, sb = x4 - r;
                    d[o0 + x4] = s0 * inv; s0 += s[o0 + ad] - s[o0 + sb];
                    d[o1 + x4] = s1 * inv; s1 += s[o1 + ad] - s[o1 + sb];
                    d[o2 + x4] = s2 * inv; s2 += s[o2 + ad] - s[o2 + sb];
                    d[o3 + x4] = s3 * inv; s3 += s[o3 + ad] - s[o3 + sb];
                }
                rowTail(s, d, o0, w, r, inv, s0, midEnd + 1); rowTail(s, d, o1, w, r, inv, s1, midEnd + 1);
                rowTail(s, d, o2, w, r, inv, s2, midEnd + 1); rowTail(s, d, o3, w, r, inv, s3, midEnd + 1);
            }
        }
        for (; y < h; y++) {
            var o = y * w, sum = 0, x, add, sub;
            // Порядок слагаемых сохранён в точности: повтор s[o] для x<0,
            // затем s[o+0..r].
            for (x = -r; x <= r; x++) sum += s[o + (x < 0 ? 0 : (x > w1 ? w1 : x))];
            var x2 = 0;
            for (; x2 < r && x2 < w; x2++) {
                d[o + x2] = sum * inv;
                add = x2 + r + 1; if (add > w1) add = w1;
                sum += s[o + add] - s[o];
            }
            for (; x2 <= midEnd; x2++) {
                d[o + x2] = sum * inv;
                sum += s[o + x2 + r + 1] - s[o + x2 - r];
            }
            for (; x2 < w; x2++) {
                d[o + x2] = sum * inv;
                sub = x2 - r; if (sub < 0) sub = 0;
                sum += s[o + w1] - s[o + sub];
            }
        }
    }
    /** Начало строки (окно упирается в левый край): x = 0…r−1; вернёт сумму для x = r. */
    function rowHead(s, d, o, w, r, inv) {
        var w1 = w - 1, sum = 0, x, add;
        for (x = -r; x <= r; x++) sum += s[o + (x < 0 ? 0 : (x > w1 ? w1 : x))];
        for (x = 0; x < r; x++) {
            d[o + x] = sum * inv;
            add = x + r + 1; if (add > w1) add = w1;
            sum += s[o + add] - s[o];
        }
        return sum;
    }
    /** Хвост строки (окно упирается в правый край): x = from…w−1. */
    function rowTail(s, d, o, w, r, inv, sum, from) {
        var w1 = w - 1, sub;
        for (var x = from; x < w; x++) {
            d[o + x] = sum * inv;
            sub = x - r; if (sub < 0) sub = 0;
            sum += s[o + w1] - s[o + sub];
        }
    }
    /**
     * Вертикальный проход ПОСТРОЧНО, а не поколоночно.
     *
     * Прежняя версия шла по столбцам: каждый шаг внутреннего цикла прыгал на
     * `w` чисел вперёд, то есть на килобайт, и почти каждое обращение было
     * промахом кэша. Здесь бегущие суммы всех столбцов живут в массиве `acc`
     * шириной w, а обход идёт по строкам — и чтение источника, и запись
     * результата последовательны.
     *
     * Арифметика КАЖДОГО столбца не изменилась: то же начальное окно в том же
     * порядке, те же прибавления и вычитания в порядке возрастания y. Меняется
     * только чередование столбцов между собой, а оно на числа не влияет —
     * результат бит-в-бит совпадает со старой версией (проверено на реальном
     * кадре: 96 140 из 96 140 значений).
     */
    function boxV(s, d, w, h, r, acc) {
        var inv = 1 / (2 * r + 1), h1 = h - 1, x, y, o;
        var a = (acc && acc.length >= w) ? acc : new Float64Array(w);
        for (x = 0; x < w; x++) a[x] = 0;
        for (y = -r; y <= r; y++) {
            o = (y < 0 ? 0 : (y > h1 ? h1 : y)) * w;
            for (x = 0; x < w; x++) a[x] += s[o + x];
        }
        for (var y2 = 0; y2 < h; y2++) {
            o = y2 * w;
            var ay = y2 + r + 1; if (ay > h1) ay = h1;
            var sy = y2 - r; if (sy < 0) sy = 0;
            var ao = ay * w, so = sy * w;
            // Запись строки и сдвиг окна — одним проходом по аккумулятору
            // (было два). Арифметика каждого столбца та же.
            for (x = 0; x < w; x++) {
                var ax = a[x];
                d[o + x] = ax * inv;
                a[x] = ax + (s[ao + x] - s[so + x]);
            }
        }
    }

    /**
     * Маска кожи ЛИЦА из геометрии: овал минус глаза, губы и брови.
     *
     * Запасы (`scaleAbout`) не декоративны. Глаз и зубы — яркие и
     * обесцвеченные, то есть неотличимы от блика по любому цветовому признаку;
     * голый контур MediaPipe проходит по краю века и по кромке губ, поэтому
     * без запаса ресницы и блик на губе всё равно попадают под снятие.
     */
    /**
     * Бровь с запасом — утолщённая от своей СРЕДНЕЙ линии.
     *
     * «Запас от центра» (fillPoly scaleAbout) годится выпуклому контуру —
     * глазу, губам. Бровь — дуга, и среднее её точек лежит ПОД дугой, вне
     * брови: растяжение от него сдвигает зону вверх, на лоб, а нижний край
     * брови остаётся открытым. Замер 26.09.2026 (18 лиц, полная сила): ретушь
     * меняла 2191 из 36 106 пикселей самих бровей — у всех 18 лиц — и 31 %
     * тёмных (волосяных) пикселей сразу за контуром. С этим контуром — 0 и
     * 12 %. Кольцо брови (landmarks.js): точки 0–4 — нижний край от наружного
     * кончика, 5–9 — верхний обратно, то есть i и 9−i — друг напротив друга.
     * Прежний контур от центра остаётся (он продлевает кончики вдоль дуги),
     * этот добавляется к нему.
     */
    function thickBrow(brow, s) {
        if (!brow || brow.length !== 10) return brow;
        var out = new Array(10);
        for (var i = 0; i < 5; i++) {
            var a = brow[i], b = brow[9 - i];
            var mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
            out[i] = [mx + (a[0] - mx) * s, my + (a[1] - my) * s];
            out[9 - i] = [mx + (b[0] - mx) * s, my + (b[1] - my) * s];
        }
        return out;
    }

    function skinMaskFromFaces(faces, w, h, opts) {
        opts = opts || {};
        var n = w * h;
        var mask = new Float32Array(n);
        var protect = new Float32Array(n);   // что защищаем (для отладки и UI)
        for (var f = 0; f < faces.length; f++) {
            var fc = faces[f];
            if (!fc.oval) continue;
            fillPoly(mask, w, h, fc.oval, 1, opts.ovalScale || 1.0);
        }
        for (var f2 = 0; f2 < faces.length; f2++) {
            var fc2 = faces[f2];
            var eyeS = opts.eyeScale || 1.55;
            var lipS = opts.lipScale || 1.18;
            var browS = opts.browScale || 1.35;
            fillPoly(protect, w, h, fc2.leftEye, 1, eyeS);
            fillPoly(protect, w, h, fc2.rightEye, 1, eyeS);
            fillPoly(protect, w, h, fc2.lips, 1, lipS);
            fillPoly(protect, w, h, fc2.leftBrow, 1, browS);
            fillPoly(protect, w, h, fc2.rightBrow, 1, browS);
            // Голый контур — под защитой ВСЕГДА. Растяжение от центра накрывает
            // его только у выпуклого контура, а закрытый глаз — изогнутая щель:
            // на таком лице ретушь меняла само веко (замер 26.09.2026: 10 px из
            // 162). У открытого глаза и губ голый контур лежит внутри растянутого,
            // и результат побитово прежний (17 лиц из 18 корпуса).
            fillPoly(protect, w, h, fc2.leftEye, 1);
            fillPoly(protect, w, h, fc2.rightEye, 1);
            fillPoly(protect, w, h, fc2.lips, 1);
            fillPoly(protect, w, h, thickBrow(fc2.leftBrow, browS), 1);
            fillPoly(protect, w, h, thickBrow(fc2.rightBrow, browS), 1);
        }
        // Мягкий край: жёсткая граница даёт видимый шов на щеке.
        var featherPx = Math.max(2, Math.round((opts.feather || 0.012) * Math.max(w, h)));
        var soft = boxBlur(mask, w, h, featherPx);
        var softP = boxBlur(protect, w, h, Math.max(2, Math.round(featherPx * 0.7)));
        // ЯДРО защиты обязано остаться единицей. Если брать только размытую
        // копию, центр глаза получает 0.6–0.8, маска кожи там — не ноль, и
        // блик В ГЛАЗУ всё равно частично снимается: замер показал изменение
        // 2439 защищённых пикселей из 2439. Поэтому максимум жёсткого и
        // размытого: внутри — точная единица, снаружи — плавный спад.
        for (var i = 0; i < n; i++) {
            var p = protect[i] > 0 ? 1 : Math.min(1, softP[i]);
            softP[i] = p;
            var v = soft[i] * (1 - p);
            mask[i] = v < 0 ? 0 : (v > 1 ? 1 : v);
        }
        return { mask: mask, protect: softP };
    }

    /**
     * Локальный «пол» диффузной кожи — как выглядела бы кожа здесь без блика.
     * Считается по пикселям-ВЫБРОСАМ НАОБОРОТ: берём только спокойные (не
     * блестящие) и растягиваем их на блики многомасштабным взвешенным
     * размытием. Крупные радиусы нужны потому, что блик на лбу бывает шире
     * любого разумного окна.
     */
    function diffuseFloor(pl, mask, inlier, opts) {
        opts = opts || {};
        var w = pl.w, h = pl.h, n = w * h;
        var wr = new Float32Array(n), wg = new Float32Array(n), wb = new Float32Array(n), ww = new Float32Array(n);
        for (var i = 0; i < n; i++) {
            if (mask[i] <= 0.5 || !inlier[i]) continue;
            wr[i] = pl.R[i]; wg[i] = pl.G[i]; wb[i] = pl.B[i]; ww[i] = 1;
        }
        var base = Math.max(w, h);
        // Верхняя ступень намеренно ограничена. Радиус 0.28 длинной стороны на
        // кадре 380 px — это 106 px, то есть почти глобальное среднее, посчитанное
        // тремя проходами коробчатого фильтра; вклад в результат при этом нулевой,
        // а времени на видео уходило больше всего (профиль: analyze 219 мс на кадр
        // 253×380). Ступеней хватает четырёх: их задача — добить дырки, а не
        // усреднить кадр.
        var radii = opts.radii || [0.010, 0.028, 0.070, 0.16].map(function (k) { return Math.max(2, Math.min(96, Math.round(k * base))); });
        var oR = new Float32Array(n), oG = new Float32Array(n), oB = new Float32Array(n);
        var T0 = FLOOR_T0, T1 = FLOOR_T1;

        // ── Первая ступень — на полной сетке ─────────────────────────────────
        // Она заполняет почти всю кожу, и её радиус мал: здесь важна точность.
        var br = boxBlur(wr, w, h, radii[0]), bg = boxBlur(wg, w, h, radii[0]);
        var bb = boxBlur(wb, w, h, radii[0]), bw = boxBlur(ww, w, h, radii[0]);
        var need = [], t0s = [];
        for (var j = 0; j < n; j++) {
            var t0 = densityWeight(bw[j], T0, T1);
            if (t0 >= 1) { oR[j] = br[j] / bw[j]; oG[j] = bg[j] / bw[j]; oB[j] = bb[j] / bw[j]; continue; }
            if (!nearMask(mask, w, h, j)) { oR[j] = pl.R[j]; oG[j] = pl.G[j]; oB[j] = pl.B[j]; continue; }
            need.push(j); t0s.push(t0);
        }

        // ── Остальные ступени — плавно и на уменьшенной сетке ────────────────
        // ПЛАВНО. Прежде пиксель брал значение первой ступени, где плотность
        // опор больше 0.02, и на границе ступеней пол СКАЧКОМ менял уровень —
        // от соседних опор к среднему по пол-лица. Снятие блика сравнивает кожу
        // с полом, поэтому скачок проступал в выдаче: концентрические полосы и
        // «пятак» на лбу вокруг одинокого островка опор (замер 26.09.2026 на
        // живых кадрах: на maria_016 бледный круг ~40 px с резкой кромкой,
        // 25–45 % маски лица заполнялось ступенями 3–4). Теперь вклад ступени
        // — гладкая функция плотности её опор: где опор вдоволь (≥ T1) — ровно
        // первая ступень, как раньше; где их мало — плавный переход к
        // следующей. Итог на 10 живых кадрах с известным ответом (синтетический
        // блик на лбу, носу, щеке): ступеньки пола p99.9 14.1 → 5.3 % на
        // пиксель, снятая доля блика 0.48 → 0.53, утечка на чистую кожу та же;
        // T1 подобран перебором 0.1 / 0.25 / 0.5 (tests/js/face-retouch-floor-and-blur.test.mjs).
        // НА УМЕНЬШЕННОЙ СЕТКЕ. Пол съедал 38 % времени кадра — 16 размытий на
        // вызов, два вызова на кадр, из них 12 с радиусами 11…61 px. Такое поле
        // гладкое по построению, поэтому считается на сетке ×2…×8 с радиусом под
        // ту же НАКОПЛЕННУЮ дисперсию каскада (coarseCascade) и читается
        // билинейно — только в пикселях, которым пол вообще нужен (маска > 0.01
        // и соседи: модель растягивается билинейно).
        if (need.length) {
            var levels = [];
            if (radii.length > 1) {
                var plan = coarseCascade(radii), cur = { R: br, G: bg, B: bb, W: bw, w: w, h: h, f: 1 };
                for (var lv = 1; lv < radii.length; lv++) {
                    var st = plan[lv];
                    var g = st.f / cur.f;
                    if (g > 1) {
                        cur = {
                            R: downsample(cur.R, cur.w, cur.h, g), G: downsample(cur.G, cur.w, cur.h, g),
                            B: downsample(cur.B, cur.w, cur.h, g), W: downsample(cur.W, cur.w, cur.h, g),
                            w: Math.ceil(cur.w / g), h: Math.ceil(cur.h / g), f: st.f,
                        };
                    }
                    cur = {
                        R: boxBlur(cur.R, cur.w, cur.h, st.r), G: boxBlur(cur.G, cur.w, cur.h, st.r),
                        B: boxBlur(cur.B, cur.w, cur.h, st.r), W: boxBlur(cur.W, cur.w, cur.h, st.r),
                        w: cur.w, h: cur.h, f: cur.f,
                    };
                    levels.push(cur);
                }
            }
            for (var t = 0; t < need.length; t++) {
                var p = need[t], px = p % w, py = (p - px) / w;
                // Снизу вверх: самая широкая ступень (или сам пиксель, если опор
                // нет нигде), затем каждая следующая поверх с весом своей плотности.
                var vr = pl.R[p], vg = pl.G[p], vb = pl.B[p];
                for (var L = levels.length - 1; L >= 0; L--) {
                    var c = levels[L];
                    var sw = sampleAt(c.W, c.w, c.h, c.f, px, py);
                    var a = L === levels.length - 1 ? (sw > T0 ? 1 : 0) : densityWeight(sw, T0, T1);
                    if (a <= 0) continue;
                    var ur = sampleAt(c.R, c.w, c.h, c.f, px, py) / sw;
                    var ug = sampleAt(c.G, c.w, c.h, c.f, px, py) / sw;
                    var ub = sampleAt(c.B, c.w, c.h, c.f, px, py) / sw;
                    vr = a * ur + (1 - a) * vr; vg = a * ug + (1 - a) * vg; vb = a * ub + (1 - a) * vb;
                }
                var a0 = t0s[t];
                if (a0 > 0) {
                    vr = a0 * (br[p] / bw[p]) + (1 - a0) * vr;
                    vg = a0 * (bg[p] / bw[p]) + (1 - a0) * vg;
                    vb = a0 * (bb[p] / bw[p]) + (1 - a0) * vb;
                }
                oR[p] = vr; oG[p] = vg; oB[p] = vb;
            }
        }
        return { R: oR, G: oG, B: oB };
    }

    // Порог плотности опор: ниже T0 ступень не участвует (как прежний порог
    // 0.02), выше T1 — берётся целиком; между — гладкая ступенька.
    var FLOOR_T0 = 0.02, FLOOR_T1 = 0.25;
    function densityWeight(d, t0, t1) {
        if (!(d > t0)) return 0;
        if (d >= t1) return 1;
        var x = (d - t0) / (t1 - t0);
        return x * x * (3 - 2 * x);
    }

    /** Нужен ли пиксель модели: маска > 0.01 в нём или у соседа (билинейная растяжка). */
    function nearMask(mask, w, h, p) {
        if (mask[p] > 0.01) return true;
        var x = p % w, y = (p - x) / w;
        var x0 = x > 0 ? x - 1 : 0, x1 = x < w - 1 ? x + 1 : w - 1;
        var y0 = y > 0 ? y - 1 : 0, y1 = y < h - 1 ? y + 1 : h - 1;
        for (var yy = y0; yy <= y1; yy++) for (var xx = x0; xx <= x1; xx++) if (mask[yy * w + xx] > 0.01) return true;
        return false;
    }

    // ── Крупные радиусы — на уменьшенной сетке ──────────────────────────────
    // Дисперсия тройного бокса радиуса r — r(r+1) px²; уменьшение блоками g×g
    // добавляет (g²−1)/12 клеток той сетки, билинейное чтение — f²/6 px².
    // Радиус на грубой сетке подбирается под ту же дисперсию; сетка берётся
    // самая грубая, где ошибка дисперсии ≤ 5 % (иначе — лучшая в пределах
    // 10 %), а радиус там ≥ 4 (меньше — тройной бокс уже плохо приближает
    // гауссиану). Сетки ×4 и ×8 по цене одинаково ничтожны рядом с полным
    // разрешением, поэтому точность здесь дешевле скорости: r=42 на ×8 давал
    // +7.2 % дисперсии и 1.4 % диапазона поля отклонения, на ×4 — −2.3 %.
    var COARSE_TOL = 0.10, COARSE_GOOD = 0.05, COARSE_MIN_R = 4, COARSE_MAX_F = 8;

    function fitRadius(need) {
        // r(r+1) ≈ need → r = (−1 + √(1+4·need)) / 2
        return Math.max(1, Math.round((-1 + Math.sqrt(1 + 4 * Math.max(0, need))) / 2));
    }

    /** План каскада пола: для каждой ступени {f, r} по НАКОПЛЕННОЙ дисперсии. */
    function coarseCascade(radii) {
        var plan = [{ f: 1, r: radii[0] }];
        var target = radii[0] * (radii[0] + 1), have = target, fPrev = 1;
        for (var k = 1; k < radii.length; k++) {
            target += radii[k] * (radii[k] + 1);
            var cands = [];
            for (var f = COARSE_MAX_F; f >= fPrev; f /= 2) {
                var g = f / fPrev;
                var down = f === fPrev ? 0 : (g * g - 1) / 12 * fPrev * fPrev;
                var r = fitRadius((target - have - down) / (f * f));
                var got = have + down + f * f * r * (r + 1);
                // На своей же сетке радиус не ограничен ничем, кроме точности.
                if (f > fPrev && r < COARSE_MIN_R) continue;
                cands.push({ f: f, r: r, got: got, err: Math.abs(got - target) / target });
            }
            var pick = pickPlan(cands);
            plan.push({ f: pick.f, r: pick.r });
            have = pick.got; fPrev = pick.f;
        }
        return plan;
    }

    /** Самая грубая сетка с ошибкой ≤ 5 %, иначе самая точная в пределах 10 %, иначе самая мелкая. */
    function pickPlan(cands) {
        for (var i = 0; i < cands.length; i++) if (cands[i].err <= COARSE_GOOD) return cands[i];
        var best = null;
        for (var j = 0; j < cands.length; j++) if (cands[j].err <= COARSE_TOL && (!best || cands[j].err < best.err)) best = cands[j];
        return best || cands[cands.length - 1];
    }

    /** Среднее по блокам g×g (у края — по тем пикселям, что есть). */
    function downsample(src, w, h, g) {
        var w2 = Math.ceil(w / g), h2 = Math.ceil(h / g), out = new Float32Array(w2 * h2);
        for (var y2 = 0; y2 < h2; y2++) {
            var ya = y2 * g, yb = Math.min(h, ya + g);
            for (var x2 = 0; x2 < w2; x2++) {
                var xa = x2 * g, xb = Math.min(w, xa + g), s = 0;
                for (var y = ya; y < yb; y++) { var o = y * w; for (var x = xa; x < xb; x++) s += src[o + x]; }
                out[y2 * w2 + x2] = s / ((yb - ya) * (xb - xa));
            }
        }
        return out;
    }

    /** Билинейное чтение сетки ×f в пикселе (px, py) полной сетки (центры клеток). */
    function sampleAt(lo, lw, lh, f, px, py) {
        if (f === 1) return lo[py * lw + px];
        var u = (px + 0.5) / f - 0.5, v = (py + 0.5) / f - 0.5;
        var x0 = Math.floor(u), y0 = Math.floor(v), tx = u - x0, ty = v - y0;
        if (x0 < 0) { x0 = 0; tx = 0; } else if (x0 >= lw - 1) { x0 = lw - 1; tx = 0; }
        if (y0 < 0) { y0 = 0; ty = 0; } else if (y0 >= lh - 1) { y0 = lh - 1; ty = 0; }
        var x1 = x0 + 1 < lw ? x0 + 1 : x0, y1 = y0 + 1 < lh ? y0 + 1 : y0;
        var a = lo[y0 * lw + x0] + (lo[y0 * lw + x1] - lo[y0 * lw + x0]) * tx;
        var b = lo[y1 * lw + x0] + (lo[y1 * lw + x1] - lo[y1 * lw + x0]) * tx;
        return a + (b - a) * ty;
    }

    /**
     * Размытие с крупным радиусом: на грубой сетке, если поле от этого не
     * меняется (та же дисперсия в пределах COARSE_TOL), иначе — как было.
     * Результат — на полной сетке.
     */
    function blurCoarse(src, w, h, r) {
        r = Math.max(1, Math.round(r));
        var target = r * (r + 1), cands = [];
        for (var f = COARSE_MAX_F; f >= 2; f /= 2) {
            var extra = (f * f - 1) / 12 + f * f / 6;
            var rr = fitRadius((target - extra) / (f * f));
            if (rr < COARSE_MIN_R) continue;
            cands.push({ f: f, r: rr, err: Math.abs(f * f * rr * (rr + 1) + extra - target) / target });
        }
        cands.push({ f: 1, r: r, err: 0 });
        var pick = pickPlan(cands);
        if (pick.f === 1) return boxBlur(src, w, h, r);
        var lw = Math.ceil(w / pick.f), lh = Math.ceil(h / pick.f);
        var lo = boxBlur(downsample(src, w, h, pick.f), lw, lh, pick.r);
        return upsample(lo, lw, lh, w, h, pick.f);
    }

    /** Грубая сетка ×f → полная, билинейно (центры клеток). */
    function upsample(lo, lw, lh, w, h, f) {
        var out = new Float32Array(w * h);
        var cx0 = new Int32Array(w), cx1 = new Int32Array(w), ctx = new Float32Array(w);
        for (var x = 0; x < w; x++) {
            var u = (x + 0.5) / f - 0.5, i0 = Math.floor(u), t = u - i0;
            if (i0 < 0) { i0 = 0; t = 0; } else if (i0 >= lw - 1) { i0 = lw - 1; t = 0; }
            cx0[x] = i0; cx1[x] = i0 + 1 < lw ? i0 + 1 : i0; ctx[x] = t;
        }
        for (var y = 0; y < h; y++) {
            var v = (y + 0.5) / f - 0.5, j0 = Math.floor(v), s = v - j0;
            if (j0 < 0) { j0 = 0; s = 0; } else if (j0 >= lh - 1) { j0 = lh - 1; s = 0; }
            var r0 = j0 * lw, r1 = (j0 + 1 < lh ? j0 + 1 : j0) * lw, o = y * w;
            for (var x2 = 0; x2 < w; x2++) {
                var a0 = cx0[x2], a1 = cx1[x2], tt = ctx[x2];
                var pa = lo[r0 + a0] + (lo[r0 + a1] - lo[r0 + a0]) * tt;
                var pb = lo[r1 + a0] + (lo[r1 + a1] - lo[r1 + a0]) * tt;
                out[o + x2] = pa + (pb - pa) * s;
            }
        }
        return out;
    }

    /**
     * Цвет света. Блик — ДОБАВКА поверх диффузной кожи, поэтому его цвет —
     * направление разности (наблюдаемое − локальный пол) на пикселях, где
     * добавка велика.
     *
     * Замеренная альтернатива «схождение лучей» (все лучи γ→σ обязаны сойтись
     * в λ) геометрически изящнее, но на корпусе дала ошибку 0.215 против 0.063:
     * направления коротких лучей тонут в 8-битном шуме. Взято то, что точнее.
     */
    function estimateLight(pl, mask, floor, opts) {
        opts = opts || {};
        var w = pl.w, h = pl.h, n = w * h;
        var ex = [];
        for (var i = 0; i < n; i++) {
            if (mask[i] <= 0.5) continue;
            var e = (0.2126 * pl.R[i] + 0.7152 * pl.G[i] + 0.0722 * pl.B[i])
                - (0.2126 * floor.R[i] + 0.7152 * floor.G[i] + 0.0722 * floor.B[i]);
            if (e > 0) ex.push(e);
        }
        var fallback = { L: [1 / 3, 1 / 3, 1 / 3], confident: false, n: 0 };
        if (ex.length < 150) return fallback;
        ex.sort(function (a, b) { return a - b; });
        var cut = ex[Math.floor(ex.length * (opts.quantile || 0.97))];
        if (!(cut > 0.008)) return fallback;
        var sr = 0, sg = 0, sb = 0, cnt = 0;
        for (var j = 0; j < n; j++) {
            if (mask[j] <= 0.5) continue;
            var dr = pl.R[j] - floor.R[j], dg = pl.G[j] - floor.G[j], db = pl.B[j] - floor.B[j];
            if (dr < 0 || dg < 0 || db < 0) continue;
            if (0.2126 * dr + 0.7152 * dg + 0.0722 * db < cut) continue;
            sr += dr; sg += dg; sb += db; cnt++;
        }
        if (cnt < 40) { fallback.n = cnt; return fallback; }
        var t = sr + sg + sb;
        if (t < EPS) return fallback;
        var L = [sr / t, sg / t, sb / t];

        // ПРАВДОПОДОБИЕ. Свет реальных сцен — солнце, небо, лампа, вспышка,
        // экран — лежит около планковской кривой: доля каждого канала примерно
        // в 0.25..0.45. Оценка вне этой полосы означает, что в выборку попало
        // не отражение источника, а что-то ещё (чаще всего выбитый в белое фон
        // у самого края маски). Замерено: на портрете против светлого фона
        // оценка уезжала в [0.195, 0.379, 0.426] — свет почти без красного,
        // чего в природе не бывает. Такую оценку нельзя применять молча:
        // вычитание цвета, которого не было, красит лицо в дополнительный
        // оттенок. Поэтому — притягиваем к нейтрали и снимаем уверенность.
        var lo = 0.24, hi = 0.46;
        var out = { L: L, confident: true, n: cnt, clamped: false };
        if (L[0] < lo || L[0] > hi || L[1] < lo || L[1] > hi || L[2] < lo || L[2] > hi) {
            var k = 0.45;   // насколько верим подозрительной оценке
            out.L = [
                L[0] * k + (1 / 3) * (1 - k),
                L[1] * k + (1 / 3) * (1 - k),
                L[2] * k + (1 / 3) * (1 - k),
            ];
            var s2 = out.L[0] + out.L[1] + out.L[2];
            out.L = [out.L[0] / s2, out.L[1] / s2, out.L[2] / s2];
            out.confident = false;
            out.clamped = true;
            out.raw = L;
        }
        return out;
    }

    /**
     * Разбор кадра: маска, пол, свет, разделимость, доля выбитого.
     * Всё, что нужно для снятия блика, считается ОДИН раз — у видео это
     * даёт и скорость, и главное: НЕИЗМЕННОСТЬ модели между кадрами, то есть
     * отсутствие мерцания, на котором ломаются бьюти-фильтры.
     */
    function analyze(pl, faces, opts) {
        opts = opts || {};
        var w = pl.w, h = pl.h, n = w * h;
        // Маску можно ПРИНЕСТИ СВОЮ. Так работает лейн «лицо не найдено»: там
        // границу кожи задаёт цвет, а не геометрия (см. skin-mask.js). Всё
        // остальное — пол диффузной кожи, цвет света, разделимость, снятие —
        // от происхождения маски не зависит вовсе, и разводить ради этого
        // вторую копию разбора значило бы гарантированно её рассинхронить.
        var mk = opts.mask
            ? { mask: opts.mask, protect: opts.protect || new Float32Array(n) }
            : skinMaskFromFaces(faces || [], w, h, opts);
        var mask = mk.mask;

        // ВНУТРЕННЯЯ маска для СТАТИСТИКИ. Применяем ретушь по мягкому краю
        // (иначе виден шов), но цвет кожи и цвет света считаем строго внутри:
        // на границе овала лежат волосы, ухо и фон, а фон у портрета сплошь и
        // рядом выбит в белое — попав в выборку, он один и определяет «цвет
        // света». Отступ берётся долей кадра, чтобы не зависеть от размера.
        var erodePx = Math.max(2, Math.round((opts.coreErode === undefined ? 0.018 : opts.coreErode) * Math.max(w, h)));
        var core = boxBlur(mask, w, h, erodePx);
        for (var e = 0; e < n; e++) core[e] = core[e] > 0.92 ? mask[e] : 0;

        var skinPx = 0;
        for (var i = 0; i < n; i++) if (mask[i] > 0.5) skinPx++;
        var corePx = 0;
        for (var i2 = 0; i2 < n; i2++) if (core[i2] > 0.5) corePx++;
        // Слишком маленькое лицо в кадре: внутренней области не осталось —
        // считаем по всей маске, но помечаем, что оценка слабее.
        var statMask = corePx > 400 ? core : mask;
        if (skinPx < 200) {
            return { ok: false, reason: 'no-skin', mask: mask, protect: mk.protect, skinPx: skinPx };
        }

        // Начальные «спокойные» пиксели: ниже верхнего квантиля яркости кожи.
        var lum = new Float32Array(n);
        var vals = [];
        for (var j = 0; j < n; j++) {
            lum[j] = 0.2126 * pl.R[j] + 0.7152 * pl.G[j] + 0.0722 * pl.B[j];
            if (statMask[j] > 0.5) vals.push(lum[j]);
        }
        vals.sort(function (a, b) { return a - b; });
        // Нижняя отсечка по яркости остаётся: внутри овала лежат ноздри, тени
        // под губой, щетина и брови — темнее кожи И другого оттенка. Взяв «всё
        // темнее порога», оценщик цвета кожи получал в основном ИХ, γ уезжала
        // в насыщенный красный, и разница «наблюдаемое минус кожа» ложно
        // приписывалась блику: на реальном снимке это давало оранжевый нос.
        var loCut = vals[Math.floor(vals.length * (opts.darkQuantile === undefined ? 0.25 : opts.darkQuantile))];

        // ── Опора — НАСЫЩЕННОСТЬ, а не яркость ────────────────────────────
        // Выбирать «спокойную кожу» верхним квантилем ЯРКОСТИ можно, только
        // если блик занимает меньшую часть кожи. Замер это опроверг: на лице,
        // где блик покрывает заметную долю щёк и лба, порог 75-го квантиля
        // сам оказывается ВНУТРИ блика, блик попадает в оценку кожи, разницы
        // не остаётся — и алгоритм снимал 2–4% вместо ожидаемого. Отказ был
        // тихий: картинка просто «почти не менялась».
        //
        // Устойчивый признак даёт физика: блик добавляет цвет ИСТОЧНИКА, то
        // есть ОБЕСЦВЕЧИВАЕТ кожу, притягивая её хроматичность к λ. Значит
        // наименее испорчены самые НАСЫЩЕННЫЕ пиксели кожи, и их доля не
        // зависит от того, сколько площади занимает блик.
        var sat = new Float32Array(n);
        var satVals = [];
        for (var s0 = 0; s0 < n; s0++) {
            if (statMask[s0] <= 0.5) continue;
            var T0 = pl.R[s0] + pl.G[s0] + pl.B[s0];
            if (T0 < EPS) continue;
            var mx = Math.max(pl.R[s0], Math.max(pl.G[s0], pl.B[s0])) / T0;
            sat[s0] = mx;                       // максимальная хроматичность
            if (lum[s0] >= loCut) satVals.push(mx);
        }
        satVals.sort(function (a, b) { return a - b; });
        // Порог выбран перебором на дорожке с известным ответом (0.45 / 0.70 /
        // 0.85): 0.70 снимает больше всего блика И меньше портит чистую кожу.
        // Слишком мягкий порог впускает в опору сам блик, слишком строгий —
        // оставляет мало точек, и поле кожи начинает угадываться.
        var satCut = satVals.length
            ? satVals[Math.floor(satVals.length * (opts.satQuantile === undefined ? 0.70 : opts.satQuantile))]
            : 0;
        var inlier = new Uint8Array(n);
        for (var k = 0; k < n; k++) {
            inlier[k] = (statMask[k] > 0.5 && lum[k] >= loCut && sat[k] >= satCut) ? 1 : 0;
        }

        // Устойчивое уточнение: посчитали блик — исключили блестящие — пересчитали пол.
        // Сглаженные плоскости нужны и позже, при повторном зажиме в deshine:
        // граница обязана считаться от той же гладкой величины, иначе она
        // снова упрётся в текстуру. Считаются ОДИН раз, до уточнения: та же
        // тройка размытий нужна specularField на каждом проходе, и прежде
        // кадр видео считал её трижды (два прохода поля блика + эта).
        var capR2 = Math.max(2, Math.round((opts.capSmooth === undefined ? 0.006 : opts.capSmooth) * Math.max(w, h)));
        var smooth = {
            R: boxBlur(pl.R, w, h, capR2),
            G: boxBlur(pl.G, w, h, capR2),
            B: boxBlur(pl.B, w, h, capR2),
            r: capR2,
        };
        var sfOpts = {};
        for (var ok in opts) sfOpts[ok] = opts[ok];
        sfOpts.capPlanes = smooth;

        var floor = diffuseFloor(pl, mask, inlier, opts);
        // Свет можно ЗАДАТЬ снаружи — так работает видео: цвет источника
        // измеряется один раз по выборке кадров всего ролика и дальше
        // фиксируется. Покадровая переоценка того же света и есть источник
        // мерцания, на котором ломаются бьюти-фильтры.
        var light = opts.light || estimateLight(pl, statMask, floor, opts);
        var passes = opts.robustPasses === undefined ? 2 : opts.robustPasses;
        for (var p = 0; p < passes; p++) {
            var S = specularField(pl, mask, floor, light.L, sfOpts);
            var sv = [];
            for (var q = 0; q < n; q++) if (statMask[q] > 0.5) sv.push(S[q]);
            sv.sort(function (a, b) { return a - b; });
            var sCut = sv[Math.floor(sv.length * 0.75)] || 0;
            var changed = 0;
            for (var r2 = 0; r2 < n; r2++) {
                var was = inlier[r2];
                // Тёмная отсечка сохраняется и в уточнении: иначе ноздри и
                // щетина возвращаются в оценку на втором проходе.
                inlier[r2] = (statMask[r2] > 0.5 && S[r2] <= sCut && lum[r2] >= loCut) ? 1 : 0;
                // Если уточнение оставило слишком мало опор (бывает, когда
                // блик широкий и S почти везде положительно), возвращаем
                // насыщенностный отбор — он от площади блика не зависит.
                if (!inlier[r2] && sat[r2] >= satCut && statMask[r2] > 0.5 && lum[r2] >= loCut && S[r2] <= sCut * 1.5) inlier[r2] = 1;
                if (was !== inlier[r2]) changed++;
            }
            floor = diffuseFloor(pl, mask, inlier, opts);
            if (!opts.light) light = estimateLight(pl, statMask, floor, opts);
            if (!changed) break;
        }

        // Разделимость и выбитое — то, что определяет, ЧТО МОЖНО обещать.
        var sepSum = 0, sepCnt = 0, blown = 0, partial = 0;
        var lam = light.L;
        for (var s2 = 0; s2 < n; s2++) {
            if (mask[s2] <= 0.5) continue;
            var T = floor.R[s2] + floor.G[s2] + floor.B[s2];
            if (T > EPS) {
                var gr = floor.R[s2] / T, gg = floor.G[s2] / T, gb = floor.B[s2] / T;
                sepSum += Math.sqrt((gr - lam[0]) * (gr - lam[0]) + (gg - lam[1]) * (gg - lam[1]) + (gb - lam[2]) * (gb - lam[2]));
                sepCnt++;
            }
            var c = pl.clip[s2];
            if (c === 7) blown++;
            else if (c) partial++;
        }
        return {
            ok: true, mask: mask, core: core, protect: mk.protect, floor: floor, light: light, smooth: smooth,
            skinPx: skinPx,
            separability: sepCnt ? sepSum / sepCnt : 0,
            blownFrac: blown / skinPx,
            partialFrac: partial / skinPx,
            faces: (faces || []).length,
        };
    }

    /**
     * Поле величины блика. Наклон (инвариантен к затенению) в паре с границей
     * «не ниже локального пола» (устойчива), затем СГЛАЖИВАНИЕ — потому что
     * блик по кадру плавный, а шум квантования нет.
     */
    function specularField(pl, mask, floor, lam, opts) {
        opts = opts || {};
        var w = pl.w, h = pl.h, n = w * h;
        var minSep = opts.minSeparation === undefined ? 0.010 : opts.minSeparation;
        var rawS = new Float32Array(n), rawW = new Float32Array(n);

        // Граница «не ниже пола» СЧИТАЕТСЯ ОТ СГЛАЖЕННОЙ яркости. Пол — сам по
        // себе гладкая величина, а наблюдаемый пиксель несёт текстуру кожи;
        // сравнивая шумное с гладким, граница упирается в поры, а не в физику.
        // Замерено на синтетике с известным ответом: истинный блик 0.226,
        // оценка 0.069, а после такой границы вычиталось 0.020 — то есть
        // ограничитель срезал ВТРОЕ больше, чем ошибался сам оценщик.
        var capR = Math.max(2, Math.round((opts.capSmooth === undefined ? 0.006 : opts.capSmooth) * Math.max(w, h)));
        // `capPlanes` — те же размытые плоскости, уже посчитанные analyze()
        // (model.smooth): берутся, только если радиус совпал.
        var cp = opts.capPlanes && opts.capPlanes.r === capR ? opts.capPlanes : null;
        var sR = cp ? cp.R : boxBlur(pl.R, w, h, capR), sG = cp ? cp.G : boxBlur(pl.G, w, h, capR), sB = cp ? cp.B : boxBlur(pl.B, w, h, capR);

        for (var i = 0; i < n; i++) {
            var m = mask[i];
            if (m <= 0.01) continue;
            var I0 = pl.R[i], I1 = pl.G[i], I2 = pl.B[i];
            var fT = floor.R[i] + floor.G[i] + floor.B[i];
            if (fT < EPS) continue;
            var g0 = floor.R[i] / fT, g1 = floor.G[i] / fT, g2 = floor.B[i] / fT;

            var dx = g0 - lam[0], dy = g1 - lam[1], dz = g2 - lam[2];
            var sep = Math.sqrt(dx * dx + dy * dy + dz * dz);
            if (sep < minSep) continue;      // физически неразделимо

            var c = pl.clip[i];
            var live0 = (c & 1) === 0, live1 = (c & 2) === 0, live2 = (c & 4) === 0;
            var nLive = (live0 ? 1 : 0) + (live1 ? 1 : 0) + (live2 ? 1 : 0);
            if (nLive < 2) continue;

            // Наклон: t_k = I_k/γ_k = d + S·(λ_k/γ_k)
            var sw = 0, sx = 0, sy = 0, sxx = 0, sxy = 0;
            for (var k = 0; k < 3; k++) {
                var live = k === 0 ? live0 : k === 1 ? live1 : live2;
                if (!live) continue;
                var gk = k === 0 ? g0 : k === 1 ? g1 : g2;
                if (gk < 0.02) continue;
                var rho = lam[k] / gk;
                var t = (k === 0 ? I0 : k === 1 ? I1 : I2) / gk;
                sw++; sx += rho; sy += t; sxx += rho * rho; sxy += rho * t;
            }
            var dd = sw * sxx - sx * sx;
            if (sw < 2 || Math.abs(dd) < 1e-9) continue;
            var S = (sw * sxy - sx * sy) / dd;
            if (!(S > 0)) continue;

            // Граница: снятое не должно опускать живой канал ниже пола.
            var cap = Infinity;
            for (var k2 = 0; k2 < 3; k2++) {
                var lv = k2 === 0 ? live0 : k2 === 1 ? live1 : live2;
                if (!lv) continue;
                var fk = k2 === 0 ? floor.R[i] : k2 === 1 ? floor.G[i] : floor.B[i];
                var Ik = k2 === 0 ? sR[i] : k2 === 1 ? sG[i] : sB[i];   // сглаженная яркость
                var cand = (Ik - fk) / Math.max(lam[k2], EPS);
                if (cand < cap) cap = cand;
            }
            if (!(cap > 0)) continue;
            // Граница «не ниже локального пола» помогает там, где оценка
            // наклона шумит, и МЕШАЕТ там, где пол неверен: нос ярче щеки по
            // ЗАТЕНЕНИЮ (он повёрнут к свету), а сглаженный пол приносит на
            // него темноту щеки — граница разрешает снять лишнее, и нос
            // уходит в оранжевый. Оценщик наклона к затенению инвариантен и в
            // яркости пола не нуждается вовсе, поэтому вес границы —
            // настройка, а не константа.
            if (opts.useCap !== false && S > cap) S = cap;

            rawS[i] = S;
            rawW[i] = Math.min(1, sep / (minSep * 4)) * (nLive === 3 ? 1 : 0.5) * m;
        }

        var rad = Math.max(2, Math.round((opts.specSmooth === undefined ? 0.010 : opts.specSmooth) * Math.max(w, h)));
        var num = new Float32Array(n);
        for (var z = 0; z < n; z++) num[z] = rawS[z] * rawW[z];
        var bn = boxBlur(num, w, h, rad), bd = boxBlur(rawW, w, h, rad);
        var out = new Float32Array(n);
        for (var y2 = 0; y2 < n; y2++) out[y2] = bd[y2] > 1e-4 ? bn[y2] / bd[y2] : 0;
        return out;
    }

    /** Снять блик. Возвращает НОВЫЕ плоскости; вход не меняется. */
    /**
     * Бликовая зона для досъёма вершин (сила выше 100 %): сглаженное поле
     * блика S выше PEAK_T0 — начало, выше PEAK_T1 — полностью.
     */
    var PEAK_T0 = 0.01, PEAK_T1 = 0.04;

    function deshine(pl, model, params) {
        params = params || {};
        var amount = params.amount === undefined ? 1 : params.amount;
        var w = pl.w, h = pl.h, n = w * h;
        var out = {
            w: w, h: h, clip: pl.clip,
            R: Float32Array.from(pl.R), G: Float32Array.from(pl.G), B: Float32Array.from(pl.B),
        };
        if (!model || !model.ok || amount <= 0) return out;
        var mask = model.mask, floor = model.floor, lam = model.light.L;
        var S = model.spec || (model.spec = specularField(pl, mask, floor, lam, params));
        for (var i = 0; i < n; i++) {
            var m = mask[i];
            if (m <= 0.01) continue;
            var s = S[i];
            if (!(s > 0)) continue;

            // ПРЕДОХРАНИТЕЛЬ ПОСЛЕ СГЛАЖИВАНИЯ. Внутри specularField величина
            // зажимается «не ниже локального пола», но затем поле размывается,
            // и пиксель может получить от соседей БОЛЬШЕ, чем сам выдерживает.
            // Без повторного зажима результат уходит ниже кожи, а поскольку
            // вычитается цвет СВЕТА, перебор красит лицо в дополнительный
            // оттенок — на голубоватом свете это видимый оранжевый налёт на
            // носу и щеке. Замечено глазом на реальном снимке; числа молчали.
            if (params.useCap !== false && model.smooth) {
                var c = pl.clip[i];
                for (var k = 0; k < 3; k++) {
                    if (c & (1 << k)) continue;   // обрезанный канал не мерило
                    var fk = k === 0 ? floor.R[i] : k === 1 ? floor.G[i] : floor.B[i];
                    var Ik = k === 0 ? model.smooth.R[i] : k === 1 ? model.smooth.G[i] : model.smooth.B[i];
                    var cand = (Ik - fk) / Math.max(lam[k], EPS);
                    if (cand < s) s = cand;
                }
            }
            // Выше 100 % («сильнее обычного»): сглаженное поле недооценивает
            // ВЕРШИНУ блика (поле и предохранитель считаются по размытым данным,
            // иначе съедается текстура кожи), и на 100 % в бликовых зонах живых
            // кадров остаётся около половины избытка над кожей. Сверх 100 %
            // вершина дотягивается к СОБСТВЕННОМУ полу пикселя — по самому
            // тугому неурезанному каналу, поэтому ниже кожи результат не уходит
            // и цветного налёта нет (множитель поверх предохранителя его давал:
            // пикселей ниже пола 3.2 → 4.7 %). Только в бликовых зонах — вне их
            // собственный избыток пикселя это текстура кожи, её не трогаем.
            if (amount > 1) {
                var sPx = Infinity, cx = pl.clip[i];
                for (var kx = 0; kx < 3; kx++) {
                    if (cx & (1 << kx)) continue;
                    var fx = kx === 0 ? floor.R[i] : kx === 1 ? floor.G[i] : floor.B[i];
                    var Ix = kx === 0 ? pl.R[i] : kx === 1 ? pl.G[i] : pl.B[i];
                    var ex = (Ix - fx) / Math.max(lam[kx], EPS);
                    if (ex < sPx) sPx = ex;
                }
                var gate = S[i] <= PEAK_T0 ? 0 : (S[i] >= PEAK_T1 ? 1 : (S[i] - PEAK_T0) / (PEAK_T1 - PEAK_T0));
                if (sPx > s && sPx < Infinity) s += Math.min(1, amount - 1) * gate * (sPx - s);
                s *= m;
            } else {
                s *= amount * m;
            }
            if (!(s > 0)) continue;

            out.R[i] = Math.max(0, pl.R[i] - s * lam[0]);
            out.G[i] = Math.max(0, pl.G[i] - s * lam[1]);
            out.B[i] = Math.max(0, pl.B[i] - s * lam[2]);
        }
        return out;
    }

    /**
     * Билинейная растяжка поля. `reg` — необязательная ОБЛАСТЬ источника
     * {x,y,w,h} в его же координатах: так полноразмерная работа делается
     * только вокруг лица, а не по всему кадру.
     */
    function rescaleField(src, sw, sh, dw, dh, reg) {
        return rescaleWith(rescalePlan(sw, sh, dw, dh, reg), src);
    }

    /**
     * Координаты растяжки — один раз на модель, а не на каждое из восьми полей:
     * столбцы и строки источника и их веса от поля не зависят (профиль кадра:
     * растяжка 8.5 %, половина — пересчёт тех же индексов). Веса хранятся в
     * Float64Array, поэтому числа выдачи — ровно прежние.
     */
    function rescalePlan(sw, sh, dw, dh, reg) {
        var rx = reg ? reg.x : 0, ry = reg ? reg.y : 0;
        var rw = reg ? reg.w : sw, rh = reg ? reg.h : sh;
        var fx = rw / dw, fy = rh / dh;
        var X0 = new Int32Array(dw), X1 = new Int32Array(dw), TX = new Float64Array(dw);
        for (var x = 0; x < dw; x++) {
            var sx = rx + (x + 0.5) * fx - 0.5;
            var x0 = Math.floor(sx), tx = sx - x0;
            if (x0 < 0) { x0 = 0; tx = 0; }
            X0[x] = x0; X1[x] = x0 + 1 < sw ? x0 + 1 : sw - 1; TX[x] = tx;
        }
        var R0 = new Int32Array(dh), R1 = new Int32Array(dh), TY = new Float64Array(dh);
        for (var y = 0; y < dh; y++) {
            var sy = ry + (y + 0.5) * fy - 0.5;
            var y0 = Math.floor(sy), ty = sy - y0;
            if (y0 < 0) { y0 = 0; ty = 0; }
            R0[y] = y0 * sw; R1[y] = (y0 + 1 < sh ? y0 + 1 : sh - 1) * sw; TY[y] = ty;
        }
        return { dw: dw, dh: dh, X0: X0, X1: X1, TX: TX, R0: R0, R1: R1, TY: TY };
    }

    function rescaleWith(pl, src) {
        var dw = pl.dw, dh = pl.dh, out = new Float32Array(dw * dh);
        var X0 = pl.X0, X1 = pl.X1, TX = pl.TX;
        for (var y = 0; y < dh; y++) {
            var r0 = pl.R0[y], r1 = pl.R1[y], ty = pl.TY[y], o = y * dw;
            for (var x = 0; x < dw; x++) {
                var x0 = X0[x], x1 = X1[x], tx = TX[x];
                var a = src[r0 + x0] + (src[r0 + x1] - src[r0 + x0]) * tx;
                var b = src[r1 + x0] + (src[r1 + x1] - src[r1 + x0]) * tx;
                out[o + x] = a + (b - a) * ty;
            }
        }
        return out;
    }

    /**
     * Модель, посчитанная на рабочем разрешении, → полное разрешение.
     *
     * Так можно ровно потому, что ВСЕ поля модели гладкие по построению:
     * маска идёт от геометрии и растушёвана, поле блика намеренно сглажено,
     * пол — многомасштабное среднее. Текстура кожи в них не участвует, а
     * значит растяжка ничего не теряет. Разбор полноразмерного снимка занял бы
     * секунды и на телефоне съел бы память, а результат был бы тот же.
     */
    function rescaleModel(model, dw, dh, sw, sh, reg, lean) {
        if (!model || !model.ok) return model;
        var plan = rescalePlan(sw, sh, dw, dh, reg);
        var f = function (a) { return a ? rescaleWith(plan, a) : a; };
        // `lean` — режим видео: core и protect нужны только для отладки и
        // подсказок в интерфейсе, а растяжка каждого поля стоит прохода по
        // всем пикселям кадра. На покадровой обработке это заметно.
        var skip = function (a) { return lean ? null : f(a); };
        return {
            ok: true,
            mask: f(model.mask), core: skip(model.core), protect: skip(model.protect),
            floor: { R: f(model.floor.R), G: f(model.floor.G), B: f(model.floor.B) },
            smooth: model.smooth ? { R: f(model.smooth.R), G: f(model.smooth.G), B: f(model.smooth.B) } : null,
            spec: model.spec ? f(model.spec) : null,
            light: model.light,
            skinPx: model.skinPx * (dw * dh) / (sw * sh),
            separability: model.separability,
            blownFrac: model.blownFrac, partialFrac: model.partialFrac,
            faces: model.faces, faceWidth: model.faceWidth,
            rescaled: true,
        };
    }

    root.FREngine = {
        rescaleField: rescaleField, rescaleModel: rescaleModel,
        toPlanes: toPlanes, toImageData: toImageData,
        skinMaskFromFaces: skinMaskFromFaces,
        analyze: analyze, deshine: deshine, specularField: specularField,
        boxBlur: boxBlur, boxBlurSparse: boxBlurSparse, blurCoarse: blurCoarse, fillPoly: fillPoly, _coarseCascade: coarseCascade, _diffuseFloor: diffuseFloor,
        _s2l: S2L, _lin2srgb: lin2srgb, _lin2byte: lin2byte,
    };
})(typeof window !== 'undefined' ? window : globalThis);
